import { Box, Grid2, Paper, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { resetBinaryChartData } from "./Common/CommonFunctions";
import EditVisualizationDialog from "./BinaryChart/EditVisualizationDialog";
import CreateVisualizationDialog from "./BinaryChart/CreateVisualizationDialog";
import BinarySignalChart from "./BinaryChart/BinarySignalChart";
import ChartActions from "../DigitalTwinV3/Tabs/BatteryVitals/ChartActions";
import DownloadCSV from "../../Helper/CSVOperations/DownloadCSV";
import { toTitleCase } from "../../Helper/Formatters/TextFormatter";
import BarGraphIcon from "./Common/BarGraphIcon";
import { IDDisplayBox } from "../../Theme/DigitalTwinStyles";
import { useTranslation } from "react-i18next";

const BinaryChartVisualization = ({ batteryID, selectedDate, selectedPrimaryGranularity, batteryInfo = {}, selectedEntity = null }) => {

    const { t } = useTranslation(['common', 'digital_twin']);
    const isDisabled = !Boolean(batteryID);
    const [chartData, setChartData] = useState({});
    const [entities, setEntities] = useState({});
    const [showCreateComponent, setShowCreateComponent] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const handleSaveChart = async (entitiesData) => {
        setIsLoading(true);

        try {
            const resetChartData = await resetBinaryChartData(entitiesData, selectedDate?.value);
            setEntities(entitiesData);
            setChartData(resetChartData);
        } finally {
            setIsLoading(false);
        }
    }

    const refreshAllCharts = async () => {
        if ((entities ?? []).length) {
            setIsLoading(true);
            try {
                await handleSaveChart(entities);
            } finally {
                setIsLoading(false);
            }
        }
    }

    const getCsvName = () => {
        let csvName = `Categorical Signals - ${toTitleCase(selectedPrimaryGranularity)}`;
        csvName = `${csvName}.csv`;
        return csvName;
    }

    const downloadChartdata = async () => {
        try {
            const csvRows = {};
            Object.values(chartData).forEach(({ title, segments }) => {
                segments.forEach(({ timestamp, status }) => {
                    if (!csvRows[timestamp]) {
                        csvRows[timestamp] = { "Date/Time": timestamp };
                    }
                    csvRows[timestamp][title] = toTitleCase(status);
                });
            });

            // array sorted by timestamp
            const csvData = Object.values(csvRows);
            DownloadCSV(getCsvName(), csvData);
        } catch (err) {
            console.log(err)
        }
    };

    useEffect(() => {
        setChartData({});
        setEntities({});
    }, [batteryID]);

    useEffect(() => {
        const chartLength = Object.keys(chartData).length
        // Don't show create component if we're loading (even if chartData is empty)
        setShowCreateComponent(chartLength <= 0 && !isLoading);
    }, [chartData, isLoading]);

    useEffect(() => {
        refreshAllCharts();
    }, [selectedDate]);

    return (
        <Grid2 container flexDirection='column' gap={1.5}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #EAEAEA", height: '520px' }}>
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', py: 2.5, px: 4 }}>
                        {!showCreateComponent && (
                            <Grid2 container justifyContent={'flex-end'} alignItems={'center'} gap={1}>
                                {(selectedEntity?.type === 'licensePlateNo' || selectedEntity?.type === 'vin') ? (
                                    <IDDisplayBox sx={{ gap: 0 }}>
                                        <Typography variant="fw400s12c494949">{t('digital_twin:section_ids.bus_number')}:&nbsp;</Typography>
                                        <Typography variant="fw400s12c494949">{selectedEntity?.value || 'N/A'}</Typography>
                                    </IDDisplayBox>
                                ) : (
                                    <IDDisplayBox sx={{ gap: 0 }}>
                                        <Typography variant="fw400s12c494949">{t('common:device_id')}:&nbsp;</Typography>
                                        <Typography variant="fw400s12c494949">{batteryInfo?.deviceID || selectedEntity?.id || 'N/A'}</Typography>
                                    </IDDisplayBox>
                                )}
                                <EditVisualizationDialog batteryID={batteryID} selectedDate={selectedDate} entities={entities} onSaveChart={handleSaveChart} batteryInfo={batteryInfo} selectedEntity={selectedEntity} />
                                <Box>
                                    <ChartActions
                                        actions={[
                                            { type: 'nothing', label: 'Download Data', icon: 'download-file-icon', onClick: () => downloadChartdata() },
                                        ]}
                                    />
                                </Box>
                            </Grid2>
                        )}
                        {showCreateComponent ? (
                            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', flexDirection: 'column', width: '100%' }}>
                                <BarGraphIcon
                                    isSelected={!isDisabled}
                                    style={{ width: 262, height: 107 }}
                                />
                                <Typography variant="fw400s12c686868" sx={{ lineHeight: '16px', mb: 2, color: isDisabled ? '#A2A2A2' : '#686868' }}>Create your first visualisation</Typography>
                                <CreateVisualizationDialog batteryID={batteryID} selectedDate={selectedDate} onSaveChart={handleSaveChart} batteryInfo={batteryInfo} selectedEntity={selectedEntity} />
                            </Box>
                        ) : (
                            <BinarySignalChart
                                isLoading={isLoading}
                                binarySignalsData={chartData}
                                batteryInfo={batteryInfo}
                                selectedEntity={selectedEntity}
                            />
                        )}
                    </Box>
                </Paper>
            </Box>
        </Grid2>
    )
}

export default BinaryChartVisualization;
